import { Component, input, inject, LOCALE_ID, signal, OnInit, effect, DestroyRef } from '@angular/core';
import { ReactiveFormsModule, ControlContainer, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, map } from 'rxjs/operators';
import { ProductsLookupService } from '../../../../../../../services/product/lookup.service';
import {
  Attribute,
  CategoryLookup,
} from '../../../../../../../pages/admin-dashboard/products/add-sub-product/sub-products.model';
import { RefrigeratorToggale } from '../../../product-info-step/refrigerator-toggale/refrigerator-toggale';
import { DropdownArrow } from '../../../../../../../shared/ui/icons/dropdown-arrow/dropdown-arrow';
import { LookupVTO } from '../../../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';

@Component({
  selector: 'app-sub-product-info',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RefrigeratorToggale, DropdownArrow],
  templateUrl: './sub-product-info.html',
  viewProviders: [{ provide: ControlContainer, useFactory: () => inject(ControlContainer, { skipSelf: true }) }],
})
export class SubProductInfo implements OnInit {
  private localeId = inject(LOCALE_ID);
  private container = inject(ControlContainer);
  private lookupService = inject(ProductsLookupService);
  private destroyRef = inject(DestroyRef);

  categories = input<CategoryLookup[]>([]);
  selectedValues = input<Record<string, string> | null>(null);
  attributes = input<Attribute[]>([]);
  parentTitleEn = input<string>('');
  parentTitleAr = input<string>('');
  zones = input<LookupVTO[]>([]);

  isRefrigerated = signal<boolean>(false);

  onRefrigeratorToggle(val: boolean): void {
    this.isRefrigerated.set(val);
    this.form.get('isRefrigerated')?.setValue(val);
    this.form.get('isRefrigerated')?.markAsTouched();
  }

  protected isRtl = signal(this.localeId.startsWith('ar'));
  protected localCategories = signal<CategoryLookup[]>([]);
  protected localSubCategories = signal<CategoryLookup[]>([]);
  protected loadingSubs = signal(false);

  constructor() {
    effect(() => {
      const vals = this.selectedValues();
      const attrs = this.attributes();
      if (vals && attrs.length > 0) this.generateTitles(vals, attrs);
    });

    effect(
      () => {
        if (this.categories().length > 0) {
          this.localCategories.set(this.categories());
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    if (this.categories().length === 0) {
      this.lookupService
        .getLookupCategories()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => this.localCategories.set(res.list || []),
          error: () => console.error('Failed to load categories'),
        });
    }

    const categoryControl = this.form.get('category');
    if (categoryControl) {
      categoryControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
        this.form.get('subCategory')?.setValue('', { emitEvent: false });
        this.localSubCategories.set([]);
        if (!val) return;
        this.loadingSubs.set(true);
        this.lookupService
          .getLookupSubCategories(Number(val))
          .pipe(
            map((res) => (res.list ?? []).map((item: any) => ({ ...item, titleAr: item.titleAr ?? item.titleEn }))),
            finalize(() => this.loadingSubs.set(false)),
          )
          .subscribe({
            next: (res) => this.localSubCategories.set(res),
            error: () => console.error('Lookup failed'),
          });
      });
    }
  }

  get form(): FormGroup {
    return this.container.control as FormGroup;
  }
  isArabic = () => document.documentElement.dir === 'rtl';

  err(field: string, errorKey: string): boolean {
    const c = this.form.get(field);
    return !!(c?.hasError(errorKey) && c?.touched);
  }

  invalid(field: string): boolean {
    const c = this.form.get(field);
    return !!(c?.invalid && c?.touched);
  }

  private generateTitles(vals: Record<string, string>, attrs: Attribute[]): void {
    const en: string[] = [];
    const ar: string[] = [];
    attrs.forEach((attr) => {
      const key = attr.attributeKey.titleEn ?? '';
      const id = vals[key];
      if (!id) return;
      const valMatch = attr.attributeValues.find((v) => String(v.id) === String(id));
      if (valMatch) {
        en.push(valMatch.titleEn ?? '');
        ar.push(valMatch.titleAr || valMatch.titleEn || '');
      }
    });
    this.form.patchValue(
      {
        titleEn: this.parentTitleEn() ? [this.parentTitleEn(), ...en].filter(Boolean).join(' - ') : '',
        titleAr: [this.parentTitleAr() || this.parentTitleEn(), ...ar].filter(Boolean).join(' - '),
      },
      { emitEvent: false },
    );
  }
}
